
import React from 'react';
import { Product } from '../../../types';
import { Coffee, ShoppingBag, ChevronRight, Plus, Zap } from 'lucide-react';
import { StoreImage } from '../../ui/StoreImage';

interface CardProps {
  product: Product & { boostAmount?: number };
  onAddToCart: (product: Product) => void;
}

export const CardFloat: React.FC<CardProps> = ({ product, onAddToCart }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  
  let displayPrice = product.price;
  if (hasVariants) {
      const prices = product.variants!.map(v => v.price);
      displayPrice = Math.min(...prices);
  }

  const displayImage = product.images?.[0] || product.imageUrl;

  return (
    <div 
        onClick={() => onAddToCart(product)} 
        className="group relative cursor-pointer pt-14 md:pt-16 h-full mb-4 px-2"
    >
        {/* Floating Card Base */}
        <div 
            className="bg-white/80 backdrop-blur-md border border-white/60 shadow-soft rounded-[2rem] p-5 pt-14 md:pt-16 flex flex-col h-full relative overflow-visible transition-all duration-300 group-hover:bg-white group-hover:shadow-xl hover:-translate-y-1 active:scale-[0.98]"
            style={{ borderRadius: 'var(--radius-card, 24px)' }}
        >
            {/* Floating Image - Absolute positioned */}
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-28 h-28 md:w-32 md:h-32 rounded-full shadow-lg overflow-hidden border-[6px] border-white z-10 bg-stone-50 group-hover:scale-105 transition-transform duration-500 ease-out group-hover:shadow-2xl">
                {displayImage ? (
                    <StoreImage src={displayImage} width={256} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-300"><Coffee size={32}/></div>
                )}
            </div>

            {/* Boost Badge - sticker overlapping the floating image's top-right edge */}
            {product.boostAmount && product.boostAmount > 0 && (
                <div className="absolute -top-2 left-[calc(50%+2.2rem)] md:left-[calc(50%+2.6rem)] z-20 bg-rose-600 text-white px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1 border-2 border-white animate-pulse-slow">
                    <Zap size={9} fill="currentColor" className="text-yellow-200" />
                    +{product.boostAmount}
                </div>
            )}

            {/* Content */}
            <div className="text-center mt-2 flex-1 flex flex-col items-center">
                <h3 className="text-base md:text-lg font-heading font-bold text-stone-800 mb-2 leading-tight px-1 group-hover:text-coffee-700 transition-colors">
                    {product.name}
                </h3>
                <p className="text-xs text-stone-500 line-clamp-2 mb-4 font-medium leading-relaxed px-2 opacity-80">
                    {product.description}
                </p>
                
                <div className="mt-auto pt-4 flex items-center justify-center gap-4 w-full border-t border-dashed border-stone-100">
                    <span className="text-lg font-black text-coffee-600 font-sans tracking-tight">
                        {displayPrice} kr
                    </span>
                    <button className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center shadow-lg hover:bg-coffee-600 transition-all hover:scale-110 active:scale-95">
                        {hasVariants ? <ChevronRight size={18} /> : <Plus size={20} />}
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};
